/**
 * F8b (#3991) — should the boot sequence proactively force the databases into the
 * Offline state, once the first reachability probe has settled?
 *
 * Why this is needed at all. The connection state machine only ever publishes a
 * *change*. `OnlineStateService` starts `_connectionState` at an Offline **placeholder**
 * and then runs the first reachability probe. On a device that boots genuinely offline
 * (airplane mode) that probe also settles on Offline — so the state goes Offline ->
 * Offline, `setConnectionStateAsync` early-returns, and **no `connectionStateChangedEvent`
 * is ever published**.
 *
 * `ConnectivityResumeFlow`'s `onOnlineStatusChangedAsync` is the only caller of
 * `setAllDatabasesStatusOffline()`, and it is purely event-driven. No event means the
 * database layer is never told. It meanwhile sets `Syncing` (`commitQueriesAsync`), and
 * during the *initial* sync the `error`/`denied` handlers deliberately do NOT set Offline
 * — they defer to `assureInitialSyncAsync`'s app-level retry. So the raw cloud state
 * latches on `Syncing`, the debounced state follows it, and the header animates "Syncing"
 * forever instead of showing the offline state. That is exactly what the owner observed on
 * native iOS in airplane mode, while the device log already said `NWPath status=unsatisfied`.
 *
 * The caller must await the first probe before asking this. Reading the connection state
 * straight away would still see the Offline placeholder on a perfectly healthy online boot,
 * and we would mark a good boot offline on every launch.
 */
export function shouldForceDatabasesOfflineAtBoot(input: {
  /** The connection state after the first reachability probe settled: true when the probe
   *  actually reached our server. */
  connectionOnline: boolean;
  /** Does the database layer already consider itself Offline? If it does, seeding again
   *  would be a redundant no-op. If it is Offline for a reason of its own while we are
   *  online (say an auth error), that is the sync error handlers' business, not ours. */
  cloudStateAlreadyOffline: boolean;
}): boolean {
  return !input.connectionOnline && !input.cloudStateAlreadyOffline;
}
